import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_API_URL } from "../../../utils/consts";
import { ClientApartment, ApartmentExtra, ReservationExtra, validatePromoCode, PromoValidateResult, AptPackage, getPublicAptPackages } from "../../../utils/api/apartmentRentalsAPI";
import { MealSelection } from "../../../utils/api/mealsAPI";
import { usePublicLang } from "../PublicLangContext";
import {
  CalDate, computeStay, getWindowCutoffDate, windowCutoffLabel, isoOf,
} from "./bookingPricing";
import { DayMap } from "./Calendar";
import { Lightbox } from "./PhotoGallery";
import BookingHeader from "./BookingHeader";
import BookingProgress, { StepDef } from "./BookingProgress";
import ApartmentSummaryCard from "./ApartmentSummaryCard";
import StepDates from "./StepDates";
import StepGuests from "./StepGuests";
import StepExtras from "./StepExtras";
import StepDetails from "./StepDetails";
import StepSummary from "./StepSummary";
import BookingSummarySidebar from "./BookingSummarySidebar";
import MobileBookingBar from "./MobileBookingBar";
import ConfirmationScreen from "./ConfirmationScreen";
import "./BookingPage.scss";

type StepKey = "dates" | "guests" | "extras" | "details" | "summary";

const BookingPage: React.FC = () => {
  const { t, lang } = usePublicLang();
  const monthNames: string[] = t("monthNames");
  const dowLabels: string[] = t("dow");
  const shortMonths: string[] = t("shortMonths");

  const calLabels = {
    free: t("free"),
    occupied: t("occupied"),
    event: lang === "en" ? "Event" : "Događaj",
    discount: t("discount"),
    specialPrice: t("specialPrice"),
    selected: t("selected"),
    notAvailable: t("notAvailable"),
    notAvailableEvent: t("notAvailableEvent"),
    notAvailableBooking: t("notAvailableBooking"),
    pastDate: t("pastDate"),
  };

  const translateLabel = (label: string): string => {
    if (label === "__standardPrice__") return t("standardPrice");
    if (label === "__discount__") return t("discount");
    if (label === "__specialPrice__") return t("specialPrice");
    return label;
  };

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get("clientId") || "";
  const referralCode = params.get("ref") || null;

  const preCheckIn = params.get("checkIn") || "";
  const preCheckOut = params.get("checkOut") || "";

  const parsePreDate = (s: string): CalDate | null => {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return null;
    return { year: y, month: m, day: d };
  };

  const [apt, setApt] = useState<ClientApartment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [aptPackages, setAptPackages] = useState<AptPackage[]>([]);
  const [applyPackage, setApplyPackage] = useState<boolean | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<AptPackage | null>(null);
  const [childrenAges, setChildrenAges] = useState<number[]>([]);

  const [promoInput, setPromoInput] = useState("");
  const [promoResult, setPromoResult] = useState<PromoValidateResult | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const now = new Date();
  const initYear = preCheckIn ? Number(preCheckIn.split("-")[0]) : now.getFullYear();
  const initMonth = preCheckIn ? Number(preCheckIn.split("-")[1]) : now.getMonth() + 1;
  const [calYear, setCalYear] = useState(initYear);
  const [calMonth, setCalMonth] = useState(initMonth);
  const [availability, setAvailability] = useState<DayMap>({});
  const availabilityCacheRef = useRef<Record<string, DayMap>>({});
  const [calLoading, setCalLoading] = useState(false);
  const [rangeError, setRangeError] = useState<string | null>(null);

  const [checkIn, setCheckIn] = useState<CalDate | null>(parsePreDate(preCheckIn));
  const [checkOut, setCheckOut] = useState<CalDate | null>(parsePreDate(preCheckOut));
  const [selectingPhase, setSelectingPhase] = useState<"checkin" | "checkout">(
    preCheckIn && preCheckOut ? "checkin" : preCheckIn ? "checkout" : "checkin"
  );

  const [stepKey, setStepKey] = useState<StepKey>("dates");
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    guestName: "",
    guestEmail: "",
    guestPhone: "",
    adults: 2,
    children: 0,
    note: "",
  });
  const [touched, setTouchedState] = useState<Record<string, boolean>>({});
  const [selectedExtras, setSelectedExtras] = useState<ReservationExtra[]>([]);
  const [mealSelections, setMealSelections] = useState<MealSelection[]>([]);
  const [mealsTotal, setMealsTotal] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [confirmation, setConfirmation] = useState<{
    reservationId: string;
    nights: number;
    amount: number;
    apartmentName: string;
  } | null>(null);

  const stepContentRef = useRef<HTMLDivElement>(null);

  // ─── Load apartment + packages ────────────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    axios
      .get<ClientApartment>(`${BASE_API_URL}/public/apartments/${id}`)
      .then((res) => setApt(res.data))
      .catch(() => setError(t("apartmentNotFound")))
      .finally(() => setLoading(false));
    if (clientId) {
      getPublicAptPackages(clientId, id)
        .then((res) => {
          const today = new Date().toISOString().split("T")[0];
          setAptPackages(res.data.filter((p) => p.isActive && p.validTo >= today));
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, clientId]);

  // ─── Availability ────────────────────────────────────────────────────────
  const loadAvailability = useCallback(async () => {
    if (!id) return;
    setCalLoading(true);
    try {
      const res = await axios.get(`${BASE_API_URL}/public/apartments/${id}/availability`, {
        params: { year: calYear, month: calMonth },
      });
      const dayMap = res.data.dayMap || {};
      availabilityCacheRef.current[`${calYear}-${calMonth}`] = dayMap;
      setAvailability(dayMap);
    } catch {
      // silent
    } finally {
      setCalLoading(false);
    }
  }, [id, calYear, calMonth]);

  const checkRangeConflicts = useCallback(async (ci: CalDate, co: CalDate): Promise<string | null> => {
    const monthsNeeded = new Set<string>();
    let cur = new Date(Date.UTC(ci.year, ci.month - 1, ci.day));
    const end = new Date(Date.UTC(co.year, co.month - 1, co.day));
    while (cur < end) {
      monthsNeeded.add(`${cur.getUTCFullYear()}-${cur.getUTCMonth() + 1}`);
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    const missing = Array.from(monthsNeeded).filter((k) => !availabilityCacheRef.current[k]);
    if (missing.length > 0) {
      await Promise.all(
        missing.map(async (ym) => {
          const [y, m] = ym.split("-").map(Number);
          try {
            const res = await axios.get(`${BASE_API_URL}/public/apartments/${id}/availability`, {
              params: { year: y, month: m },
            });
            availabilityCacheRef.current[ym] = res.data.dayMap || {};
          } catch { /* leave absent — will be treated as clear */ }
        })
      );
    }
    cur = new Date(Date.UTC(ci.year, ci.month - 1, ci.day));
    while (cur < end) {
      const y = cur.getUTCFullYear(), m = cur.getUTCMonth() + 1, d = cur.getUTCDate();
      const status = (availabilityCacheRef.current[`${y}-${m}`] || {})[d];
      if (status === "confirmed" || status === "pending" || status === "event") {
        return `${String(d).padStart(2, "0")}.${String(m).padStart(2, "0")}.${y}.`;
      }
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return null;
  }, [id]);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  const handleCalPrev = () => {
    if (calMonth === 1) { setCalYear((y) => y - 1); setCalMonth(12); }
    else setCalMonth((m) => m - 1);
  };
  const handleCalNext = () => {
    if (calMonth === 12) { setCalYear((y) => y + 1); setCalMonth(1); }
    else setCalMonth((m) => m + 1);
  };
  const canGoPrev = calYear > now.getFullYear() || (calYear === now.getFullYear() && calMonth > now.getMonth() + 1);

  const minNights = apt?.minNights || 1;
  const windowCutoff = apt ? getWindowCutoffDate(apt) : null;
  const windowLabel = apt ? windowCutoffLabel(apt, shortMonths, lang) : null;

  const canGoNext = (() => {
    if (!windowCutoff) return true;
    const nextMonth = calMonth === 12 ? 1 : calMonth + 1;
    const nextYear = calMonth === 12 ? calYear + 1 : calYear;
    return new Date(nextYear, nextMonth - 1, 1) <= windowCutoff;
  })();

  // Calendar clicks select NIGHTS, not a checkin/checkout point pair: clicking a day while
  // choosing checkout means "I stay this night too". The actual departure date (checkOut —
  // what's stored, priced, and sent to the backend) is always the night after the last clicked
  // day, computed here. This keeps the checkOut/nights contract used everywhere else (pricing,
  // availability, backend) completely unchanged — only the calendar's day→checkOut mapping moves.
  const dateFromMs = (ms: number): CalDate => {
    const dt = new Date(ms);
    return { year: dt.getFullYear(), month: dt.getMonth() + 1, day: dt.getDate() };
  };

  const handleSelectDay = async (y: number, m: number, d: number) => {
    if (selectingPhase === "checkin") {
      setCheckIn({ year: y, month: m, day: d });
      setCheckOut(null);
      setSelectingPhase("checkout");
      setPromoResult(null);
      setRangeError(null);
      return;
    }
    const ci = checkIn!;
    const ciMs = new Date(ci.year, ci.month - 1, ci.day).getTime();
    const clickedMs = new Date(y, m - 1, d).getTime();
    if (clickedMs <= ciMs) {
      setCheckIn({ year: y, month: m, day: d });
      setCheckOut(null);
      setSelectingPhase("checkout");
      setRangeError(null);
      return;
    }
    // The clicked day is the last night stayed — departure is the following day.
    const departureMs = clickedMs + 86400000;
    const selectedNights = Math.round((departureMs - ciMs) / 86400000);
    if (selectedNights < minNights) {
      let minDate = new Date(ciMs + minNights * 86400000);
      if (windowCutoff && minDate > windowCutoff) minDate = windowCutoff;
      const proposedCo = { year: minDate.getFullYear(), month: minDate.getMonth() + 1, day: minDate.getDate() };
      const conflict = await checkRangeConflicts(ci, proposedCo);
      if (conflict) {
        setRangeError(`Nije moguće rezervisati — datum ${conflict} je zauzet ili nedostupan u odabranom periodu. Odaberite druge datume.`);
        setCheckIn(null); setCheckOut(null); setSelectingPhase("checkin");
        return;
      }
      setRangeError(null);
      setCheckOut(proposedCo);
      setSelectingPhase("checkin");
    } else {
      let proposedCo = dateFromMs(departureMs);
      if (windowCutoff && new Date(proposedCo.year, proposedCo.month - 1, proposedCo.day) > windowCutoff) {
        proposedCo = { year: windowCutoff.getFullYear(), month: windowCutoff.getMonth() + 1, day: windowCutoff.getDate() };
      }
      const conflict = await checkRangeConflicts(ci, proposedCo);
      if (conflict) {
        setRangeError(`Nije moguće rezervisati — datum ${conflict} je zauzet ili nedostupan u odabranom periodu. Odaberite druge datume.`);
        setCheckIn(null); setCheckOut(null); setSelectingPhase("checkin");
        return;
      }
      setRangeError(null);
      setCheckOut(proposedCo);
      setSelectingPhase("checkin");
    }
  };

  const resetDates = () => {
    setCheckIn(null);
    setCheckOut(null);
    setSelectingPhase("checkin");
    setPromoResult(null);
    setRangeError(null);
    setApplyPackage(null);
    setSelectedPackage(null);
    setChildrenAges([]);
  };

  // Manual date entry — an alternative to clicking the calendar for guests who don't pick up
  // on the click-to-select interaction. Unlike calendar clicks, typed dates are unambiguous
  // (the check-out field IS the departure date), so no "last night" translation is needed here.
  const handleTypeCheckIn = (iso: string) => {
    if (!iso) { resetDates(); return; }
    const [y, m, d] = iso.split("-").map(Number);
    setCheckIn({ year: y, month: m, day: d });
    setCheckOut(null);
    setSelectingPhase("checkout");
    setCalYear(y);
    setCalMonth(m);
    setRangeError(null);
    setPromoResult(null);
  };

  const handleTypeCheckOut = async (iso: string) => {
    if (!iso || !checkIn) return;
    const [y, m, d] = iso.split("-").map(Number);
    const ciMs = new Date(checkIn.year, checkIn.month - 1, checkIn.day).getTime();
    const coMs = new Date(y, m - 1, d).getTime();
    if (coMs <= ciMs) return;
    const typedNights = Math.round((coMs - ciMs) / 86400000);
    if (typedNights < minNights) {
      setRangeError(
        lang === "en"
          ? `Minimum stay is ${minNights} ${minNights === 1 ? "night" : "nights"}. Choose a later check-out date.`
          : `Minimalan boravak je ${minNights} ${minNights === 1 ? "noć" : "noći"}. Odaberite kasniji datum odlaska.`
      );
      return;
    }
    const proposedCo = { year: y, month: m, day: d };
    const conflict = await checkRangeConflicts(checkIn, proposedCo);
    if (conflict) {
      setRangeError(`Nije moguće rezervisati — datum ${conflict} je zauzet ili nedostupan u odabranom periodu. Odaberite druge datume.`);
      return;
    }
    setRangeError(null);
    setCheckOut(proposedCo);
    setSelectingPhase("checkin");
    setCalYear(y);
    setCalMonth(m);
  };

  const toggleExtra = (extra: ApartmentExtra) => {
    const eid = extra._id || extra.name;
    setSelectedExtras((prev) => {
      const exists = prev.find((e) => e.extraId === eid);
      if (exists) return prev.filter((e) => e.extraId !== eid);
      const price = extra.discountedPrice != null ? extra.discountedPrice : extra.regularPrice;
      return [...prev, { extraId: eid, name: extra.name, quantity: 1, pricePerUnit: price, total: price }];
    });
  };

  const updateExtraQty = (extraId: string, qty: number) => {
    setSelectedExtras((prev) => prev.map((e) => e.extraId === extraId ? { ...e, quantity: qty, total: e.pricePerUnit * qty } : e));
  };

  const handleValidatePromo = async () => {
    if (!promoInput.trim() || !apt) return;
    setPromoLoading(true);
    try {
      const res = await validatePromoCode(promoInput.trim(), clientId, totalAmount);
      setPromoResult(res.data);
    } catch (err: any) {
      setPromoResult({ valid: false, message: err?.response?.data?.message || "Nevažeći promo kod" });
    } finally {
      setPromoLoading(false);
    }
  };
  const clearPromo = () => { setPromoInput(""); setPromoResult(null); };

  // ─── Computed pricing ────────────────────────────────────────────────────
  const nights = checkIn && checkOut
    ? Math.ceil((new Date(checkOut.year, checkOut.month - 1, checkOut.day).getTime() -
        new Date(checkIn.year, checkIn.month - 1, checkIn.day).getTime()) / 86400000)
    : 0;

  const stayInfo = apt && checkIn && checkOut && nights > 0 ? computeStay(apt, checkIn, checkOut) : null;
  const nightsTotal = stayInfo?.total ?? 0;
  const extrasTotal = selectedExtras.reduce((s, e) => s + e.total, 0);

  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);
  const daysUntilCheckIn = checkIn
    ? Math.ceil((new Date(checkIn.year, checkIn.month - 1, checkIn.day).getTime() - todayMidnight.getTime()) / 86400000)
    : null;

  const matchedPackages: AptPackage[] = (() => {
    if (!checkIn || !checkOut || nights <= 0) return [];
    const ci = `${checkIn.year}-${String(checkIn.month).padStart(2, "0")}-${String(checkIn.day).padStart(2, "0")}`;
    const co = `${checkOut.year}-${String(checkOut.month).padStart(2, "0")}-${String(checkOut.day).padStart(2, "0")}`;
    return aptPackages.filter(
      (pkg) => ci >= pkg.validFrom && co <= pkg.validTo && nights >= pkg.minNights
        && (!pkg.apartmentId || (apt && String(pkg.apartmentId) === String(apt._id)))
    );
  })();

  const matchedPackageKey = matchedPackages.map((p) => p._id).join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setApplyPackage(null); setSelectedPackage(null); setChildrenAges([]); }, [matchedPackageKey]);

  const matchedPackage = selectedPackage;

  const packageNightsTotal: number | null = (() => {
    if (!matchedPackage || applyPackage !== true || nights <= 0) return null;
    const childRules = matchedPackage.childrenPricing || [];
    const hasChildRules = childRules.length > 0;
    let effectiveAdults = formData.adults || 2;
    if (hasChildRules && childrenAges.length > 0) {
      for (const age of childrenAges) {
        const rule = childRules.find((r) => age >= r.ageFrom && (r.ageTo == null || age < r.ageTo));
        if (!rule || rule.priceType === "adult") effectiveAdults++;
      }
    } else {
      effectiveAdults = (formData.adults || 2) + (formData.children || 0);
    }
    const tiers = matchedPackage.pricingByPersons;
    let baseCost: number | null = null;
    if (tiers && tiers.length > 0) {
      const sorted = [...tiers].sort((a, b) => a.persons - b.persons);
      let best = sorted[0];
      for (const tr of sorted) { if (tr.persons <= effectiveAdults) best = tr; }
      baseCost = Math.round(best.pricePerNight * nights);
    } else if (matchedPackage.discountPercent != null) {
      baseCost = Math.round(nightsTotal * (1 - matchedPackage.discountPercent / 100));
    }
    if (baseCost == null) return null;
    let childCost = 0;
    if (hasChildRules && childrenAges.length > 0) {
      for (const age of childrenAges) {
        const rule = childRules.find((r) => age >= r.ageFrom && (r.ageTo == null || age < r.ageTo));
        if (rule && rule.priceType === "fixed" && rule.pricePerNight != null) {
          childCost += rule.pricePerNight * nights;
        }
      }
    }
    return baseCost + Math.round(childCost);
  })();

  const packageChildCostRows: Array<{ label: string; amount: number }> = (() => {
    if (!matchedPackage || applyPackage !== true || nights <= 0) return [];
    const childRules = matchedPackage.childrenPricing || [];
    if (!childRules.length || !childrenAges.length) return [];
    const rows: Array<{ label: string; amount: number }> = [];
    for (const age of childrenAges) {
      const rule = childRules.find((r) => age >= r.ageFrom && (r.ageTo == null || age < r.ageTo));
      if (rule && rule.priceType === "fixed" && rule.pricePerNight != null) {
        const label = `Dijete (${age} god.) × ${nights} noć`;
        rows.push({ label, amount: Math.round(rule.pricePerNight * nights) });
      }
    }
    return rows;
  })();

  const effectiveNightsTotal = packageNightsTotal ?? nightsTotal;
  const totalAmount = effectiveNightsTotal + extrasTotal + mealsTotal;
  const promoDiscount = (promoResult?.valid && promoResult.discount) ? promoResult.discount : 0;
  const finalAmount = totalAmount - promoDiscount;

  // ─── Step machine ────────────────────────────────────────────────────────
  const hasExtrasStep = (apt?.extras?.length ?? 0) > 0 || (apt?.meals?.length ?? 0) > 0 || aptPackages.length > 0;

  const stepDefs: StepDef[] = [
    { key: "dates", label: lang === "en" ? "Dates" : "Datum" },
    { key: "guests", label: lang === "en" ? "Guests" : "Gosti" },
    ...(hasExtrasStep ? [{ key: "extras", label: lang === "en" ? "Extras" : "Dodatno" }] : []),
    { key: "details", label: lang === "en" ? "Details" : "Podaci" },
    { key: "summary", label: lang === "en" ? "Confirm" : "Potvrda" },
  ];
  const stepKeys = stepDefs.map((s) => s.key as StepKey);
  let stepIndex = stepKeys.indexOf(stepKey);
  if (stepIndex === -1) stepIndex = 0;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goToStep = (key: StepKey) => {
    setStepKey(key);
    scrollToTop();
  };

  const setTouched = (field: string) => setTouchedState((prev) => ({ ...prev, [field]: true }));

  const canContinueFromDates = !!checkIn && !!checkOut && nights >= minNights;
  const canContinueFromDetails = !!formData.guestName.trim() && !!formData.guestPhone.trim();

  const goNext = () => {
    if (stepKey === "dates") {
      if (!canContinueFromDates) { setRangeError(null); return; }
      goToStep(stepKeys[stepIndex + 1]);
      return;
    }
    if (stepKey === "details") {
      if (!canContinueFromDetails) {
        setTouchedState({ guestName: true, guestPhone: true });
        return;
      }
      goToStep(stepKeys[stepIndex + 1]);
      return;
    }
    if (stepKey === "summary") {
      handleSubmit();
      return;
    }
    const next = stepKeys[stepIndex + 1];
    if (next) goToStep(next);
  };

  const goBack = () => {
    if (stepIndex === 0) {
      clientId ? navigate(`/apartments/public?clientId=${clientId}`) : navigate(-1);
      return;
    }
    goToStep(stepKeys[stepIndex - 1]);
  };

  const setField = (field: keyof typeof formData, value: string) => {
    setFormData((f) => ({ ...f, [field]: value } as typeof formData));
  };

  const setAdults = (n: number) => {
    const maxChildren = Math.max(0, (apt?.capacity || 99) - n);
    setFormData((f) => ({ ...f, adults: n, children: Math.min(f.children, maxChildren) }));
    setChildrenAges((prev) => prev.slice(0, maxChildren));
  };
  const setChildrenCount = (n: number) => {
    setFormData((f) => ({ ...f, children: n }));
    setChildrenAges((prev) => {
      const next = [...prev];
      while (next.length < n) next.push(0);
      return next.slice(0, n);
    });
  };

  const handleSubmit = async () => {
    setSubmitError("");
    if (!checkIn || !checkOut) { setSubmitError(t("selectCheckInCheckOut")); goToStep("dates"); return; }
    if (!formData.guestName.trim()) { setSubmitError(t("enterName")); goToStep("details"); return; }
    if (!formData.guestPhone.trim()) { setSubmitError(t("enterPhone")); goToStep("details"); return; }

    setSubmitting(true);
    try {
      const res = await axios.post(`${BASE_API_URL}/public/apartments/${id}/book`, {
        guestName: formData.guestName,
        guestEmail: formData.guestEmail,
        guestPhone: formData.guestPhone,
        checkIn: `${checkIn.year}-${String(checkIn.month).padStart(2, "0")}-${String(checkIn.day).padStart(2, "0")}`,
        checkOut: `${checkOut.year}-${String(checkOut.month).padStart(2, "0")}-${String(checkOut.day).padStart(2, "0")}`,
        adults: formData.adults,
        children: formData.children,
        childrenAges: (applyPackage === true && matchedPackage?.childrenPricing?.length && childrenAges.length)
          ? childrenAges : undefined,
        note: formData.note,
        extras: selectedExtras,
        mealSelections: mealSelections.length > 0 ? mealSelections : undefined,
        referralCode,
        promoCode: promoResult?.valid ? promoInput.trim().toUpperCase() : undefined,
        packageId: applyPackage === true && matchedPackage ? matchedPackage._id : undefined,
      });
      setConfirmation(res.data);
      setIsSuccess(true);
      setSelectedExtras([]);
      setMealSelections([]);
      setMealsTotal(0);
      scrollToTop();

      const months = new Set<string>();
      let cur = new Date(Date.UTC(checkIn.year, checkIn.month - 1, checkIn.day));
      const end = new Date(Date.UTC(checkOut.year, checkOut.month - 1, checkOut.day));
      while (cur <= end) {
        months.add(`${cur.getUTCFullYear()}-${cur.getUTCMonth() + 1}`);
        cur.setUTCMonth(cur.getUTCMonth() + 1);
      }
      await Promise.all(
        Array.from(months).map(async (ym) => {
          const [y, m] = ym.split("-").map(Number);
          const r = await axios.get(`${BASE_API_URL}/public/apartments/${id}/availability`, { params: { year: y, month: m } });
          if (y === calYear && m === calMonth) setAvailability(r.data.dayMap || {});
        })
      );
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || t("errorSendingReservation"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = () => {
    setIsSuccess(false);
    setConfirmation(null);
    resetDates();
    setTouchedState({});
    setFormData((f) => ({ ...f, guestName: "", guestEmail: "", guestPhone: "", note: "" }));
    goToStep("dates");
  };

  // ─── Loading / error states ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="pab">
        <div className="pab-loading-page">
          <div className="pab-skeleton pab-skeleton--hero" />
          <div className="pab-loading-page__body">
            <div className="pab-skeleton pab-skeleton--line" style={{ width: "60%" }} />
            <div className="pab-skeleton pab-skeleton--line" style={{ width: "40%" }} />
            <div className="pab-skeleton pab-skeleton--block" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !apt) {
    return (
      <div className="pab">
        <div className="pab-error-page">
          <div className="pab-error-page__icon">🏚</div>
          <div className="pab-error-page__title">{t("apartmentNotFound")}</div>
          <div className="pab-error-page__msg">{error || t("apartmentNotActive")}</div>
          {clientId && (
            <button className="pab-cta-btn" onClick={() => navigate(`/apartments/public?clientId=${clientId}`)}>
              ← {lang === "en" ? "Back to list" : "Nazad na listu"}
            </button>
          )}
        </div>
      </div>
    );
  }

  const totalGuests = formData.adults + formData.children;

  // CTA per step
  let ctaLabel: string;
  let ctaDisabled: boolean;
  let ctaAmount: number;
  let ctaAmountLabel = lang === "en" ? "Total" : "Ukupno";
  switch (stepKey) {
    case "dates":
      ctaLabel = canContinueFromDates ? `${lang === "en" ? "Continue" : "Nastavi"} →` : t("selectDates");
      ctaDisabled = !canContinueFromDates;
      ctaAmount = nightsTotal;
      break;
    case "guests":
      ctaLabel = `${lang === "en" ? "Continue" : "Nastavi"} →`;
      ctaDisabled = false;
      ctaAmount = totalAmount;
      break;
    case "extras":
      ctaLabel = `${lang === "en" ? "Continue" : "Nastavi"} →`;
      ctaDisabled = false;
      ctaAmount = finalAmount;
      break;
    case "details":
      ctaLabel = `${lang === "en" ? "Continue" : "Nastavi"} →`;
      ctaDisabled = false;
      ctaAmount = finalAmount;
      break;
    default:
      ctaLabel = t("book");
      ctaDisabled = false;
      ctaAmount = finalAmount;
  }

  if (isSuccess && confirmation) {
    return (
      <div className="pab">
        <BookingHeader
          title={apt.name}
          backLabel={t("back")}
          onBack={() => clientId ? navigate(`/apartments/public?clientId=${clientId}`) : navigate(-1)}
        />
        <div className="pab-container pab-container--narrow">
          <ConfirmationScreen
            confirmation={confirmation}
            currency={apt.currency}
            onFinish={handleFinish}
            finishLabel={lang === "en" ? "Done" : "Završi"}
            t={t}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pab">
      {lightboxIndex !== null && apt.images?.length > 0 && (
        <Lightbox images={apt.images} name={apt.name} startAt={lightboxIndex} onClose={() => setLightboxIndex(null)} />
      )}

      <BookingHeader title={apt.name} backLabel={t("back")} onBack={goBack} />

      <BookingProgress
        steps={stepDefs}
        currentIndex={stepIndex}
        onStepClick={(i) => goToStep(stepKeys[i])}
        stepOfLabel={lang === "en" ? "Step" : "Korak"}
        ofLabel={lang === "en" ? "of" : "od"}
      />

      <div className="pab-layout">
        <div className="pab-main">
          {stepKey === "dates" && (
            <ApartmentSummaryCard
              apt={apt}
              onOpenLightbox={(idx) => setLightboxIndex(idx)}
              aboutLabel={lang === "en" ? "About this place" : "O apartmanu"}
              amenitiesLabel={lang === "en" ? "Amenities" : "Sadržaj i pogodnosti"}
              readMoreLabel={lang === "en" ? "Show more" : "Prikaži više"}
              showLessLabel={lang === "en" ? "Show less" : "Prikaži manje"}
              guestsLabel={lang === "en" ? "up to" : "do"}
              typeLabel={lang === "en" ? "Apartment" : "Apartman"}
            />
          )}

          <div ref={stepContentRef}>
            {stepKey === "dates" && (
              <StepDates
                apt={apt}
                calYear={calYear}
                calMonth={calMonth}
                availability={availability}
                calLoading={calLoading}
                checkIn={checkIn}
                checkOut={checkOut}
                nights={nights}
                minNights={minNights}
                onSelectDay={handleSelectDay}
                onPrev={handleCalPrev}
                onNext={handleCalNext}
                canGoPrev={canGoPrev}
                canGoNext={canGoNext}
                windowCutoff={windowCutoff}
                windowLabel={windowLabel}
                rangeError={rangeError}
                onResetDates={resetDates}
                monthNames={monthNames}
                dowLabels={dowLabels}
                calLabels={calLabels}
                daysUntilCheckIn={daysUntilCheckIn}
                lang={lang}
                t={t}
                selectingPhase={selectingPhase}
                onTypeCheckIn={handleTypeCheckIn}
                onTypeCheckOut={handleTypeCheckOut}
              />
            )}

            {stepKey === "guests" && (
              <StepGuests
                apt={apt}
                formData={formData}
                setAdults={setAdults}
                setChildren={setChildrenCount}
                lang={lang}
                t={t}
              />
            )}

            {stepKey === "extras" && checkIn && checkOut && (
              <StepExtras
                apt={apt}
                checkIn={checkIn}
                checkOut={checkOut}
                nights={nights}
                nightsTotal={nightsTotal}
                totalPersons={totalGuests}
                matchedPackages={matchedPackages}
                applyPackage={applyPackage}
                selectedPackage={selectedPackage}
                setApplyPackage={setApplyPackage}
                setSelectedPackage={setSelectedPackage}
                childrenCount={formData.children}
                childrenAges={childrenAges}
                setChildrenAges={setChildrenAges}
                selectedExtras={selectedExtras}
                toggleExtra={toggleExtra}
                updateExtraQty={updateExtraQty}
                onMealsChange={(sels, total) => { setMealSelections(sels); setMealsTotal(total); }}
                promoInput={promoInput}
                setPromoInput={setPromoInput}
                promoResult={promoResult}
                promoLoading={promoLoading}
                onValidatePromo={handleValidatePromo}
                onClearPromo={clearPromo}
                lang={lang}
                t={t}
              />
            )}

            {stepKey === "details" && (
              <StepDetails
                formData={formData}
                setField={setField}
                touched={touched}
                setTouched={setTouched}
                lang={lang}
                t={t}
              />
            )}

            {stepKey === "summary" && checkIn && checkOut && stayInfo && (
              <StepSummary
                apt={apt}
                checkIn={checkIn}
                checkOut={checkOut}
                nights={nights}
                adults={formData.adults}
                children={formData.children}
                stayInfo={stayInfo}
                matchedPackage={matchedPackage}
                packageNightsTotal={packageNightsTotal}
                packageChildCostRows={packageChildCostRows}
                effectiveNightsTotal={effectiveNightsTotal}
                selectedExtras={selectedExtras}
                extrasTotal={extrasTotal}
                mealSelections={mealSelections}
                mealsTotal={mealsTotal}
                promoDiscount={promoDiscount}
                finalAmount={finalAmount}
                submitError={submitError}
                translateLabel={translateLabel}
                lang={lang}
                t={t}
                showPromoInline={!hasExtrasStep}
                promoInput={promoInput}
                setPromoInput={setPromoInput}
                promoResult={promoResult}
                promoLoading={promoLoading}
                onValidatePromo={handleValidatePromo}
                onClearPromo={clearPromo}
              />
            )}
          </div>
        </div>

        <BookingSummarySidebar
          apt={apt}
          checkIn={checkIn}
          checkOut={checkOut}
          nights={nights}
          totalGuests={totalGuests}
          amount={ctaAmount}
          ctaLabel={ctaLabel}
          ctaDisabled={ctaDisabled}
          submitting={submitting}
          onContinue={goNext}
          lang={lang}
        />
      </div>

      <MobileBookingBar
        amount={ctaAmount}
        currency={apt.currency}
        amountLabel={ctaAmountLabel}
        ctaLabel={ctaLabel}
        ctaDisabled={ctaDisabled}
        submitting={submitting}
        onContinue={goNext}
        lang={lang}
      />

      <div className="pab-footer">
        <div className="pab-footer__brand">{apt.name}</div>
        <div className="pab-footer__sub">Powered by FixmanHR</div>
      </div>
    </div>
  );
};

export default BookingPage;
